/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import { Button, Form, InputNumber, Typography, Row, Col, Space } from 'antd';
import styles from '../styles.module.css';
import {
  formatWithThousandSeparator,
  parseFormattedNumber,
} from '../../../../../services/utils/commonUtils';

const { Text, Title } = Typography;
function TotalImportExportCard({
  t,
  form,
  cardType,
  title,
  broughtForwards,
  selectedOption,
  onOptionChange,
  getValidationRules,
}) {
  const isExport = cardType === 'totalExport';

  // Get current toggle value from form
  const type = selectedOption || 'Range';
  const isRange = type === 'Range';

  return (
    <Row gutter={[16, 14]} className={styles.padding20}>
      <Col span={24}>
        <Row justify="space-between" align="middle">
          <div className={styles.headerContent}>
            <Title level={5} className={styles.headerTitle}>
              {title}
            </Title>
          </div>

          <Space.Compact>
            <Button
              className={`${styles.toggleButton} ${
                isRange ? styles.activeButton : ''
              }`}
              onClick={() => onOptionChange(cardType, 'Range')}
            >
              <span className={styles.buttonText}>{t('label_readinds')}</span>
            </Button>
            <Button
              className={`${styles.toggleButton} ${
                !isRange ? styles.activeButton : ''
              }`}
              onClick={() => onOptionChange(cardType, 'Number')}
            >
              <span className={styles.buttonText}>
                {t('label_total_units')}
              </span>
            </Button>
          </Space.Compact>
        </Row>
      </Col>

      {/* Fields */}
      <Col span={24} className={styles.marginTop20}>
        {isRange ? (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Text className={styles.fieldLabel}>
                {t('label_previous_meter_reading')}
              </Text>
              <Form.Item
                className={styles.formItemNew}
                name={[cardType, 'previousReading']}
                dependencies={[[cardType, 'currentReading']]}
                rules={[
                  ...getValidationRules(
                    'previousReading',
                    'reading',
                    [cardType, 'currentReading'],
                    t,
                  ),
                  {
                    type: 'number',
                    min: 0,
                    message: t('label_meter_reading_positive_number_error'),
                  },
                ]}
              >
                <InputNumber
                  className={styles.input}
                  placeholder={t('label_enter_previous_meter_reading')}
                  min={0}
                  formatter={formatWithThousandSeparator}
                  parser={parseFormattedNumber}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Text className={styles.fieldLabel}>
                {t('label_current_meter_reading')}
              </Text>
              <Form.Item
                className={styles.formItemNew}
                name={[cardType, 'currentReading']}
                min={0}
                dependencies={[[cardType, 'previousReading']]}
                rules={[
                  ...getValidationRules(
                    'currentReading',
                    'reading',
                    [cardType, 'previousReading'],
                    t,
                  ),
                  {
                    type: 'number',
                    min: 0,
                    message: t('label_meter_reading_positive_number_error'),
                  },
                ]}
              >
                <InputNumber
                  className={styles.input}
                  placeholder={t('label_enter_current_meter_reading')}
                  min={0}
                  formatter={formatWithThousandSeparator}
                  parser={parseFormattedNumber}
                />
              </Form.Item>
            </Col>
          </Row>
        ) : (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Text className={styles.fieldLabel}>
                {t('label_number_of_units')}
              </Text>
              <Form.Item
                className={styles.formItemNew}
                name={[cardType, 'numOfUnits']}
                rules={[
                  {
                    required: true,
                    message: t('label_please_enter_number_of_units'),
                  },
                  {
                    type: 'number',
                    max: 999999,
                    message: t(
                      'label_number_of_units_should_be_less_than_million',
                    ),
                  },
                ]}
              >
                <InputNumber
                  className={styles.input}
                  min={0}
                  placeholder={t('label_enter_number_of_units')}
                  formatter={formatWithThousandSeparator}
                  parser={parseFormattedNumber}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Col>

      {/* Brought Forward (only for export) */}
      {broughtForwards && isExport && (
        <Col xs={24} md={12} className={styles.marginTop20}>
          <Text className={styles.fieldLabel}>
            {t('label_brought_forward_units')}
          </Text>
          <Form.Item
            className={styles.formItemNew}
            name={[cardType, 'broughtForward']}
            rules={[
              {
                required: true,
                message: t('label_please_enter_brought_forward_units'),
              },
              {
                type: 'number',
                max: 999999,
                message: t('label_number_of_units_should_be_less_than_million'),
              },
            ]}
          >
            <InputNumber
              className={styles.input}
              min={0}
              placeholder={t('label_enter_brought_forward_units')}
              formatter={formatWithThousandSeparator}
              parser={parseFormattedNumber}
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  );
}

export default TotalImportExportCard;
